import _ from 'lodash';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import {
  useGetRiskListOptimized,
  useGetRiskListWithEntities,
} from '@/hooks/queries';
import { useEntityLabelsFeature } from '@/hooks/useEntityLabelsFeature';
import { useEntityPath } from '@/hooks/useEntityPath';

import ControlledSelect from '../controlled-select';
import type { ControlledBaseProps } from '../types';
import { getOptions } from './selectUtils';
import { getOptionsWithEntities } from './selectUtilsWithEntities';

interface Props<T extends FieldValues> extends ControlledBaseProps<T> {
  addEmptyOption?: boolean;
  disabled?: boolean;
  single?: boolean;
  testId: string;
  showEntityLabels?: boolean;
}

export const ControlledRiskSelect = <T extends FieldValues>({
  showEntityLabels,
  ...props
}: Props<T>) => {
  const { watch } = useFormContext();
  const value = watch(props.name);
  const { shouldShowEntityLabels } = useEntityLabelsFeature(showEntityLabels);
  const { getEntityPath } = useEntityPath();

  // Use different queries based on whether we need entity information
  const { data: dataWithEntities, loading: loadingWithEntities } =
    useGetRiskListWithEntities({
      queryArgs: {},
      shouldSkip: !shouldShowEntityLabels,
    });

  const { data: dataBasic, loading: loadingBasic } = useGetRiskListOptimized({
    queryArgs: {},
    shouldSkip: shouldShowEntityLabels,
  });

  const loading = shouldShowEntityLabels ? loadingWithEntities : loadingBasic;

  // Generate options based on whether we're showing entity labels
  const options = shouldShowEntityLabels
    ? getOptionsWithEntities(dataWithEntities, value, true).map((opt) => ({
        value: opt.value,
        label: opt.label,
        description: opt.entityInfo?.entityId
          ? getEntityPath(opt.entityInfo.entityId)
          : undefined,
      }))
    : getOptions(dataBasic, value);

  return (
    <ControlledSelect
      statusType={loading ? 'loading' : 'finished'}
      {...props}
      options={options}
    />
  );
};
